import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { JobLogPatternDailySnapshotSummaryDto, JobLogPatternReportResponse, JobLogPatternService } from '@asamco/application-link/shared';
import { AsamcoUserService } from '@asamco/base';
import { showErrorSwal } from '@asamco/utils';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'appshell-link-joblog-report-admin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './link-joblog-report-admin.component.html',
  styleUrl: './link-joblog-report-admin.component.scss',
})
export class LinkJoblogReportAdminComponent implements OnInit {
	readonly quickRanges = [7, 14, 30];

  private readonly canUseJobLogPatternMatcherSubject = new BehaviorSubject<boolean>(false);
  private readonly checkingAccessSubject = new BehaviorSubject<boolean>(true);
  private readonly loadingAvailabilitySubject = new BehaviorSubject<boolean>(false);
  private readonly loadingReportSubject = new BehaviorSubject<boolean>(false);
  private readonly errorMessageSubject = new BehaviorSubject<string>('');
  private readonly summariesSubject = new BehaviorSubject<JobLogPatternDailySnapshotSummaryDto[]>([]);
  private readonly reportSubject = new BehaviorSubject<JobLogPatternReportResponse | null>(null);

  readonly vm$ = combineLatest([
    this.canUseJobLogPatternMatcherSubject,
    this.checkingAccessSubject,
    this.loadingAvailabilitySubject,
    this.loadingReportSubject,
    this.errorMessageSubject,
    this.summariesSubject,
    this.reportSubject,
  ]).pipe(
    map(([canUseJobLogPatternMatcher, checkingAccess, loadingAvailability, loadingReport, errorMessage, summaries, report]) => ({
      canUseJobLogPatternMatcher,
      checkingAccess,
      loadingAvailability,
      loadingReport,
      errorMessage,
      summaries,
      report,
      hasSnapshotData: summaries.length > 0,
      availableFromDay: summaries.length > 0 ? summaries[0].dayUtc.slice(0, 10) : '',
      availableToDay: summaries.length > 0 ? summaries[summaries.length - 1].dayUtc.slice(0, 10) : '',
    }))
  );

  fromDay = '';
  toDay = '';

  constructor(
    private readonly asamcoUserService: AsamcoUserService,
    private readonly jobLogPatternService: JobLogPatternService
  ) {}

  async ngOnInit(): Promise<void> {
    const canUseJobLogPatternMatcher = await this.asamcoUserService.hasAssignedPermission('AL3_JOBLOG_PATTERN_MATCHER');
    this.canUseJobLogPatternMatcherSubject.next(canUseJobLogPatternMatcher);
    this.checkingAccessSubject.next(false);

    if (!canUseJobLogPatternMatcher) {
      return;
    }

    this.loadAvailability();
  }

  get summaries(): JobLogPatternDailySnapshotSummaryDto[] {
    return this.summariesSubject.value;
  }

  get report(): JobLogPatternReportResponse | null {
    return this.reportSubject.value;
  }

  get hasSnapshotData(): boolean {
    return this.summaries.length > 0;
  }

  get availableFromDay(): string {
    return this.hasSnapshotData ? this.summaries[0].dayUtc.slice(0, 10) : '';
  }

  get availableToDay(): string {
    return this.hasSnapshotData ? this.summaries[this.summaries.length - 1].dayUtc.slice(0, 10) : '';
  }

  get selectedSnapshotDays(): number {
    if (!this.fromDay || !this.toDay) {
      return 0;
    }

    return this.summaries.filter(x => {
      const day = x.dayUtc.slice(0, 10);
      return day >= this.fromDay && day <= this.toDay;
    }).length;
  }

  get expectedSnapshotDays(): number {
    if (!this.fromDay || !this.toDay || this.fromDay > this.toDay) {
      return 0;
    }

    const start = new Date(`${this.fromDay}T00:00:00.000Z`).getTime();
    const end = new Date(`${this.toDay}T00:00:00.000Z`).getTime();
    return Math.floor((end - start) / 86400000) + 1;
  }

  get missingSnapshotDays(): number {
    return Math.max(0, this.expectedSnapshotDays - this.selectedSnapshotDays);
  }

  get periodComparison(): any {
    return this.report?.periodComparison ?? null;
  }

  get thirtyDaySummary(): any {
    return this.report?.thirtyDaySummary ?? null;
  }

  get allTimeSummary(): any {
    return this.report?.allTimeErrorSummary ?? this.report?.errorSummary ?? null;
  }

  get currentPeriodSummary(): any {
    return this.periodComparison?.currentPeriod ?? this.report?.errorSummary ?? null;
  }

  get previousAllTimeTotalErrors(): number {
    return Math.max(0, (this.allTimeSummary?.totalErrorLogCount ?? 0) - (this.currentPeriodSummary?.totalErrorLogCount ?? 0));
  }

  get previousAllTimeMatchedErrors(): number {
    return Math.max(0, (this.allTimeSummary?.matchedErrorLogCount ?? 0) - (this.currentPeriodSummary?.matchedErrorLogCount ?? 0));
  }

  get previousAllTimeHandledErrors(): number {
    return Math.max(0, (this.allTimeSummary?.handledErrorLogCount ?? 0) - (this.currentPeriodSummary?.handledErrorLogCount ?? 0));
  }

  get previousAllTimeUnmatchedErrors(): number {
    return Math.max(0, (this.allTimeSummary?.unmatchedErrorLogCount ?? 0) - (this.currentPeriodSummary?.unmatchedErrorLogCount ?? 0));
  }

  get previousAllTimePatterns(): number {
    return Math.max(0, (this.report?.patternSummary?.totalPatterns ?? 0) - (this.report?.patternSummary?.patternsAddedInCurrentPeriod ?? 0));
  }

  get previousAllTimeCatchRate(): number {
    return this.calculateRate(this.previousAllTimeMatchedErrors, this.previousAllTimeTotalErrors);
  }

  get currentPeriodPatternsAdded(): number {
    return this.report?.patternSummary?.patternsAddedInCurrentPeriod ?? 0;
  }

  get previousPeriodPatternsAdded(): number {
    const items = this.report?.userActivityForCurrentPeriod ?? [];
    return items.length === 0 ? 0 : Math.max(...items.map((x: any) => x.totalPatternsCreatedPreviousPeriod));
  }

  get currentNotHandledTotal(): number {
    return Math.max(0, (this.periodComparison?.currentPeriod?.matchedErrorLogCount ?? 0) - (this.periodComparison?.currentPeriod?.handledErrorLogCount ?? 0));
  }

  get previousNotHandledTotal(): number {
    return Math.max(0, (this.periodComparison?.previousPeriod?.matchedErrorLogCount ?? 0) - (this.periodComparison?.previousPeriod?.handledErrorLogCount ?? 0));
  }

  get totalAddedPatternsCurrentPeriod(): number {
    const items = this.report?.userActivityForCurrentPeriod ?? [];
    return items.length === 0 ? 0 : Math.max(...items.map((x: any) => x.totalPatternsCreatedInCurrentPeriod));
  }

  get totalAddedPatternsPreviousPeriod(): number {
    const items = this.report?.userActivityForCurrentPeriod ?? [];
    return items.length === 0 ? 0 : Math.max(...items.map((x: any) => x.totalPatternsCreatedPreviousPeriod));
  }

	loadReport(): void {
		if (!this.fromDay || !this.toDay) {
			this.errorMessageSubject.next('Select a valid period first.');
			return;
    }

    if (this.fromDay > this.toDay) {
      this.errorMessageSubject.next('The start date cannot be after the end date.');
      return;
    }

    this.errorMessageSubject.next('');
    this.loadingReportSubject.next(true);

    this.jobLogPatternService.getReport({
      fromUtc: `${this.fromDay}T00:00:00.000Z`,
      toUtc: `${this.toDay}T23:59:59.999Z`,
      topPatterns: 10,
      topUnmatchedErrors: 10,
    }).subscribe({
      next: report => {
        this.reportSubject.next(report);
        this.loadingReportSubject.next(false);
      },
      error: error => {
        this.reportSubject.next(null);
        this.errorMessageSubject.next('Failed to load the job log pattern report.');
        this.loadingReportSubject.next(false);
        showErrorSwal(error);
      },
    });
	}

	setFromDay(value: string): void {
		this.fromDay = value;
		this.clearLoadedReport();
	}

	setToDay(value: string): void {
		this.toDay = value;
		this.clearLoadedReport();
	}

	applyQuickRange(days: number): void {
		if (!this.hasSnapshotData) {
			return;
		}

		const startIndex = Math.max(0, this.summaries.length - days);
		this.fromDay = this.summaries[startIndex].dayUtc.slice(0, 10);
		this.toDay = this.summaries[this.summaries.length - 1].dayUtc.slice(0, 10);
		this.clearLoadedReport();
	}

  isQuickRangeActive(days: number): boolean {
    if (!this.hasSnapshotData || !this.fromDay || !this.toDay) {
      return false;
    }

    const startIndex = Math.max(0, this.summaries.length - days);
    return this.fromDay === this.summaries[startIndex].dayUtc.slice(0, 10)
      && this.toDay === this.summaries[this.summaries.length - 1].dayUtc.slice(0, 10);
  }

  formatNumber(value: number | null | undefined): string {
    return new Intl.NumberFormat('en-US').format(value ?? 0);
  }

  formatPercent(value: number | null | undefined): string {
    return `${(value ?? 0).toFixed(2)}%`;
  }

  formatDate(value: string | null | undefined): string {
    return value ? value.slice(0, 10) : '—';
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    }).format(date).replace(',', '');
  }

  formatRange(startUtc: string | null | undefined, endUtc: string | null | undefined): string {
    return !startUtc || !endUtc ? '—' : `${this.formatDate(startUtc)} to ${this.formatDate(endUtc)}`;
  }

  formatSignedNumber(value: number | null | undefined): string {
    const normalized = value ?? 0;
    if (normalized === 0) {
      return '0';
    }

    return `${normalized > 0 ? '+' : '−'}${this.formatNumber(Math.abs(normalized))}`;
  }

  formatSignedPercent(value: number | null | undefined): string {
    const normalized = value ?? 0;
    if (normalized === 0) {
      return '0.00%';
    }

    return `${normalized > 0 ? '+' : '−'}${Math.abs(normalized).toFixed(2)}%`;
  }

  deltaArrow(value: number | null | undefined): string {
    if ((value ?? 0) > 0) return '▲';
    if ((value ?? 0) < 0) return '▼';
    return '→';
  }

  deltaClass(value: number | null | undefined, positiveDeltaIsGood: boolean): string {
    const normalized = value ?? 0;
    if (normalized === 0) return 'delta-neutral';
    const isGood = positiveDeltaIsGood ? normalized > 0 : normalized < 0;
    return isGood ? 'delta-good' : 'delta-bad';
  }

  calculateRate(part: number | null | undefined, total: number | null | undefined): number {
    const denominator = total ?? 0;
    return denominator <= 0 ? 0 : ((part ?? 0) / denominator) * 100;
  }

  calculateDeltaRate(current: number | null | undefined, previous: number | null | undefined): number {
    const currentValue = current ?? 0;
    const previousValue = previous ?? 0;
    if (previousValue === 0) {
      return currentValue === 0 ? 0 : 100;
    }

    return ((currentValue - previousValue) / previousValue) * 100;
  }

  trackByMessage(_: number, row: any): string {
    return row.message;
  }

  trackByPattern(_: number, row: any): number {
    return row.patternId;
  }

  trackByUser(_: number, row: any): string {
    return row.username;
  }

	private loadAvailability(): void {
		this.loadingAvailabilitySubject.next(true);
		this.errorMessageSubject.next('');

		this.jobLogPatternService.getDailySnapshots().subscribe({
			next: items => {
				const summaries = [...(items ?? [])].sort((a, b) => a.dayUtc.localeCompare(b.dayUtc));
				this.summariesSubject.next(summaries);
				this.loadingAvailabilitySubject.next(false);
				this.fromDay = '';
				this.toDay = '';
				this.clearLoadedReport();

				if (summaries.length === 0) {
					return;
				}
			},
			error: error => {
				this.fromDay = '';
				this.toDay = '';
				this.summariesSubject.next([]);
				this.reportSubject.next(null);
				this.errorMessageSubject.next('Failed to load available snapshot dates.');
				this.loadingAvailabilitySubject.next(false);
				showErrorSwal(error);
			},
		});
	}

	private clearLoadedReport(): void {
		this.errorMessageSubject.next('');
		this.reportSubject.next(null);
	}
}
